import type { CSSProperties, SVGProps } from "react";

/**
 * Conjunto minimo de icones em traco unico, vindo do contrato de design
 * `docs/design-history/dashboard-operacional`. Todos compartilham a mesma
 * assinatura (`size`, `sw`, `style`) e usam `currentColor` para herdar a cor
 * do contexto.
 */

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "viewBox"> {
  size?: number;
  sw?: number;
  style?: CSSProperties;
  viewBox?: string;
  fill?: string;
}

function makeIcon(paths: string | string[], defaultSw = 1.6) {
  return function IconComponent({
    size = 16,
    sw = defaultSw,
    fill = "none",
    style,
    viewBox = "0 0 24 24",
    ...rest
  }: IconProps) {
    const list = Array.isArray(paths) ? paths : [paths];
    return (
      <svg
        width={size}
        height={size}
        viewBox={viewBox}
        fill={fill}
        stroke="currentColor"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={style}
        aria-hidden="true"
        {...rest}
      >
        {list.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    );
  };
}

export const IconArrowLeft = makeIcon(["M19 12H5", "M12 19l-7-7 7-7"]);
export const IconRefresh = makeIcon([
  "M3 12a9 9 0 0 1 15.5-6.3L21 8",
  "M21 3v5h-5",
  "M21 12a9 9 0 0 1-15.5 6.3L3 16",
  "M3 21v-5h5",
]);
export const IconEye = makeIcon([
  "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z",
  "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
]);
export const IconEdit = makeIcon([
  "M12 20h9",
  "M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z",
]);
export const IconSearch = makeIcon([
  "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z",
  "M21 21l-4.3-4.3",
]);
export const IconBell = makeIcon([
  "M18 16v-5a6 6 0 0 0-12 0v5l-2 2v1h16v-1l-2-2z",
  "M10 21a2 2 0 0 0 4 0",
]);
export const IconSettings = makeIcon([
  "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
]);
export const IconPlus = makeIcon(["M12 5v14", "M5 12h14"]);
export const IconCopy = makeIcon([
  "M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z",
  "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1",
]);
export const IconChevronRight = makeIcon("M9 6l6 6-6 6");
export const IconChevronLeft = makeIcon("M15 6l-6 6 6 6");
export const IconCalendar = makeIcon(["M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M3 10h18", "M8 2v4", "M16 2v4"]);
export const IconMore = makeIcon(
  [
    "M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
    "M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
    "M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  ],
  2.6,
);
export const IconActivity = makeIcon("M22 12h-4l-3 9L9 3l-3 9H2");
export const IconClock = makeIcon([
  "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z",
  "M12 6v6l4 2",
]);
export const IconLayers = makeIcon([
  "M12 2 2 7l10 5 10-5-10-5z",
  "M2 17l10 5 10-5",
  "M2 12l10 5 10-5",
]);
export const IconDownload = makeIcon([
  "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
  "M7 10l5 5 5-5",
  "M12 15V3",
]);
export const IconCpu = makeIcon([
  "M4 4h16v16H4z",
  "M9 9h6v6H9z",
  "M9 1v3",
  "M15 1v3",
  "M9 20v3",
  "M15 20v3",
  "M20 9h3",
  "M20 14h3",
  "M1 9h3",
  "M1 14h3",
]);
export const IconWifi = makeIcon([
  "M2 9a15 15 0 0 1 20 0",
  "M5 12.5a11 11 0 0 1 14 0",
  "M8.5 16a6 6 0 0 1 7 0",
  "M12 20h.01",
]);
export const IconBroadcast = makeIcon([
  "M4.93 4.93a10 10 0 0 0 0 14.14",
  "M19.07 4.93a10 10 0 0 1 0 14.14",
  "M7.76 7.76a6 6 0 0 0 0 8.49",
  "M16.24 7.76a6 6 0 0 1 0 8.49",
  "M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
]);
export const IconBox = makeIcon([
  "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z",
  "M3.27 6.96 12 12.01l8.73-5.05",
  "M12 22.08V12",
]);
export const IconShield = makeIcon(["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"]);
export const IconWrench = makeIcon([
  "M14.7 6.3a4 4 0 0 1 0 5.66l-1.42 1.42L8.65 8.74l1.42-1.42a4 4 0 0 1 4.63 0z",
  "M14.7 6.3 18.5 2.5",
  "M8.65 8.74 3.5 13.9l3.6 3.6 5.16-5.16",
  "M8 18l-2 2",
  "M11 21l-3-3",
]);
export const IconFilter = makeIcon("M22 3H2l8 9.5V19l4 2v-8.5L22 3z");
export const IconHash = makeIcon([
  "M4 9h16",
  "M4 15h16",
  "M10 3 8 21",
  "M16 3l-2 18",
]);
export const IconTrendUp = makeIcon(["M3 17l6-6 4 4 8-8", "M14 7h7v7"]);
export const IconLink = makeIcon([
  "M10 13a5 5 0 0 0 7 0l3-3a5 5 0 1 0-7-7l-1 1",
  "M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 1 0 7 7l1-1",
]);
export const IconAlertTri = makeIcon([
  "M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z",
  "M12 9v4",
  "M12 17h.01",
]);
export const IconGrid = makeIcon([
  "M3 3h7v7H3z",
  "M14 3h7v7h-7z",
  "M3 14h7v7H3z",
  "M14 14h7v7h-7z",
]);
export const IconX = makeIcon(["M18 6L6 18", "M6 6l12 12"]);
export const IconStar = makeIcon("m12 3 2.8 5.7 6.3.9-4.6 4.5 1.1 6.3-5.6-3-5.6 3 1.1-6.3L3 9.6l6.2-.9L12 3z");
export const IconFactory = makeIcon(["M3 21V10l6 3V7l6 3V3h4l2 18H3z", "M7 17h1", "M12 17h1", "M17 17h1"]);
export const IconFlask = makeIcon(["M9 3h6", "M10 3v6l-6 10a1.4 1.4 0 0 0 1.2 2h13.6a1.4 1.4 0 0 0 1.2-2L14 9V3", "M8 14h8"]);
export const IconFlame = makeIcon("M12 3c1 5-4 6-4 10 0 1 1 2 2 2-1-3 3-4 3-7 4 4 6 7 4 11-2 4-8 4-10 1C2 14 8 8 12 3z");
export const IconWind = makeIcon(["M3 8h12a3 3 0 1 0-3-3", "M2 12h17a3 3 0 1 1-3 3", "M4 16h5a3 3 0 1 1-3 3"]);
export const IconWater = makeIcon("M12 2s-8 9-8 14a8 8 0 0 0 16 0c0-5-8-14-8-14z");
export const IconEnergy = makeIcon("m13 2-9 12h7l-1 8 10-12h-7l1-8z");
